import { Box, Flex, HStack, Menu, Portal, Stack, Text, chakra } from '@chakra-ui/react';
import { Check, ChevronDown, Info } from 'lucide-react';

export interface SelectMenuOption<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
  description?: string;
}

interface SelectMenuProps<T extends string | number> {
  label: string;
  value: T;
  options: readonly SelectMenuOption<T>[];
  onChange: (value: T) => void;
  helper?: string;
  className?: string;
  portalled?: boolean;
}

export function SelectMenu<T extends string | number>({
  label,
  value,
  options,
  onChange,
  helper,
  className,
  portalled = true,
}: SelectMenuProps<T>) {
  const selected = options.find((option) => option.value === value) ?? options[0];
  const positioner = (
    <Menu.Positioner>
      <Menu.Content
        zIndex="popover"
        minW="11rem"
        overflow="hidden"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.subtle"
        bg="surface.panel"
        boxShadow="2xl"
      >
        {options.map((option) => {
          const selectedOption = option.value === value;
          return (
            <Menu.Item
              key={String(option.value)}
              value={String(option.value)}
              disabled={option.disabled}
              onSelect={() => {
                onChange(option.value);
              }}
              display="flex"
              minH="10"
              w="full"
              alignItems="flex-start"
              justifyContent="space-between"
              gap="3"
              px="3"
              py="2"
              textAlign="left"
              fontSize="sm"
              transition="all 0.2s"
              color={selectedOption ? 'text.primary' : 'text.muted'}
              _hover={{ bg: 'signal.blue/10', color: 'text.primary' }}
              _focus={{ bg: 'signal.blue/10', color: 'text.primary', outline: 'none' }}
              _highlighted={{ bg: 'whiteAlpha.100', color: 'text.primary' }}
              _disabled={{
                cursor: 'not-allowed',
                opacity: 0.45,
                bg: 'transparent',
                color: 'text.muted',
              }}
            >
              <Box>
                <Text as="span" display="block" fontWeight="semibold">
                  {option.label}
                </Text>
                {option.description ? (
                  <Text as="span" mt="0.5" display="block" fontSize="xs" color="text.muted/80">
                    {option.description}
                  </Text>
                ) : null}
              </Box>
              {selectedOption ? <Check size={16} color="var(--oq-colors-signal-blue)" /> : null}
            </Menu.Item>
          );
        })}
      </Menu.Content>
    </Menu.Positioner>
  );

  return (
    <Box position="relative" className={className}>
      <Menu.Root positioning={{ placement: 'bottom-start' }} lazyMount unmountOnExit>
        <Menu.Trigger asChild>
          <chakra.button
            type="button"
            title={helper}
            display="grid"
            minH="11"
            w="full"
            minW="11rem"
            gridTemplateColumns="auto 1fr auto"
            alignItems="center"
            gap="3"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="blackAlpha.100"
            px="4"
            py="2.5"
            textAlign="left"
            transition="all 0.2s"
            _hover={{ borderColor: 'signal.blue/30' }}
            _focus={{ outline: 'none', boxShadow: '0 0 0 2px var(--oq-colors-signal-blue)' }}
          >
            <HStack gap="1.5" whiteSpace="nowrap" fontSize="sm" color="text.primary">
              <Text as="span">{label}</Text>
              {helper ? (
                <Info aria-label={helper} size={14} color="var(--oq-colors-text-muted)" />
              ) : null}
            </HStack>
            <Text truncate fontSize="sm" fontWeight="semibold" color="text.primary">
              {selected?.label ?? 'Select'}
            </Text>
            <ChevronDown size={16} color="var(--oq-colors-text-muted)" />
          </chakra.button>
        </Menu.Trigger>

        {portalled ? <Portal>{positioner}</Portal> : positioner}
      </Menu.Root>
    </Box>
  );
}